import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged,
  updatePassword, reauthenticateWithCredential, EmailAuthProvider,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

const AuthContext = createContext(null);

// Staff log in with a short id (e.g. "font"); Firebase Auth needs an email, so
// we map id → a synthetic internal email. Users never see or type this.
const EMAIL_DOMAIN = 'users.flashyourmeme.app';
export const idToEmail = (id) => `${String(id || '').trim().toLowerCase()}@${EMAIL_DOMAIN}`;

const BAD_LOGIN = { success: false, error: 'ID หรือ Password ไม่ถูกต้อง' };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Firebase Auth is the source of truth for the session (persists across reloads).
  // A real (non-anonymous) user is resolved to their profile in users/{uid}.
  useEffect(() => {
    if (!auth) { setLoading(false); return; }
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser || fbUser.isAnonymous) { setUser(null); setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', fbUser.uid));
        if (snap.exists() && snap.data().active !== false) {
          const p = snap.data();
          setUser({ id: p.userId, name: p.name, role: p.role, uid: fbUser.uid });
        } else {
          // Signed in but no usable profile → treat as logged out.
          setUser(null);
        }
      } catch {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (userId, password) => {
    if (!auth) return BAD_LOGIN;
    try {
      await signInWithEmailAndPassword(auth, idToEmail(userId), password);
      return { success: true }; // onAuthStateChanged loads the profile
    } catch {
      return BAD_LOGIN;
    }
  };

  const logout = async () => {
    setUser(null);
    try { await signOut(auth); } catch {}
  };

  const isAdmin = () => user?.role === 'admin';

  const changePassword = async (currentPassword, newPassword) => {
    if (!auth?.currentUser) return { success: false, error: 'ไม่ได้เข้าสู่ระบบ' };
    if (!newPassword || newPassword.length < 6)
      return { success: false, error: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' };
    try {
      const cred = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, cred);
      await updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch {
      return { success: false, error: 'รหัสผ่านปัจจุบันไม่ถูกต้อง' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAdmin, changePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
